import { useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Activity, Ban, Gauge, Server, ShieldAlert, ShieldCheck, Table2 } from "lucide-react";
import { nodesApi, rulesApi } from "@/api/endpoints";
import { useAuth } from "@/auth/AuthProvider";
import { useLive } from "@/live/store";
import {
  attackBands,
  buildDropsChart,
  buildFilterChart,
  buildTrafficChart,
  fleetNow,
  type FilterMetric,
  type TrafficMetric,
} from "@/live/selectors";
import type { TrafficModel } from "@/live/model";
import type { Band } from "@/components/charts/TimeSeriesChart";
import { formatAgo, formatBps, formatInt, formatPps, formatSI, formatTime } from "@/lib/format";
import { refetchInterval, useNow } from "@/lib/hooks";
import { PERMISSIONS as P } from "@/lib/permissions";
import { Card, CardHeader } from "@/components/ui/Card";
import { PageHeader } from "@/components/ui/PageHeader";
import { Segmented } from "@/components/ui/Segmented";
import { Button } from "@/components/ui/Button";
import { EmptyState, Forbidden } from "@/components/ui/States";
import { Table, Td, Th, Tr } from "@/components/ui/Table";
import { StatTile } from "@/components/StatTile";
import { Legend, TimeSeriesChart, type ChartSeries } from "@/components/charts/TimeSeriesChart";
import { AttackStateBadge, surgeFactor } from "@/components/AttackBits";

function ChartTable({ xs, series, format }: { xs: number[]; series: ChartSeries[]; format: (v: number) => string }) {
  // The accessible/table view of a chart: the last 30 seconds that have data.
  const rows = xs
    .map((t, i) => ({ t, vals: series.map((s) => s.values[i] ?? null) }))
    .filter((r) => r.vals.some((v) => v !== null))
    .slice(-30)
    .reverse();
  if (rows.length === 0) return <EmptyState title="Chưa có mẫu nào" />;
  return (
    <Table className="max-h-72">
      <thead>
        <tr>
          <Th>Thời điểm</Th>
          {series.map((s) => (
            <Th key={s.key} className="text-right">
              {s.label}
            </Th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <Tr key={r.t}>
            <Td className="tabular text-ink-2">{formatTime(r.t * 1000)}</Td>
            {r.vals.map((v, i) => (
              <Td key={series[i]!.key} className="text-right tabular">
                {v === null ? "—" : format(v)}
              </Td>
            ))}
          </Tr>
        ))}
      </tbody>
    </Table>
  );
}

const FILTER_METRIC: Record<FilterMetric, { title: string; fmt: (v: number) => string }> = {
  pps: { title: "gói/giây", fmt: formatPps },
  bps: { title: "bit/giây", fmt: formatBps },
  drops: { title: "gói bị chặn/giây", fmt: (v) => `${formatSI(v)} gói/s` },
};

/** One small chart per node, a line per filter (default, minecraft-java, web, …). */
function FilterCharts({ model, nowSec, bands }: { model: TrafficModel; nowSec: number; bands: Band[] }) {
  const [metric, setMetric] = useState<FilterMetric>("pps");
  const m = FILTER_METRIC[metric];
  return (
    <Card className="xl:col-span-3">
      <CardHeader
        title="Lưu lượng theo bộ lọc của từng node"
        description="Mỗi đường là một bộ lọc. Một gói đi qua default và có thể thêm một bộ lọc riêng, nên các đường không cộng lại với nhau."
        actions={
          <Segmented
            label="Chỉ số"
            value={metric}
            onChange={setMetric}
            options={[
              { value: "pps", label: "pps" },
              { value: "bps", label: "bps" },
              { value: "drops", label: "Bị chặn" },
            ]}
          />
        }
      />
      <div className="grid gap-x-6 gap-y-5 px-4 py-4 md:grid-cols-2">
        {model.nodeOrder.map((node) => {
          const chart = buildFilterChart(model, node, metric, nowSec);
          return (
            <section key={node} className="min-w-0">
              <h3 className="mb-1.5 flex items-center gap-2 text-[13px] font-semibold">
                <Server className="size-3.5 text-ink-3" aria-hidden />
                {model.nodeNames.get(node) ?? node}
              </h3>
              <div className="mb-2">
                <Legend
                  items={chart.series.map((x) => ({
                    key: x.key,
                    label: x.label,
                    colorVar: x.colorVar,
                    value: chart.latest.has(x.key) ? m.fmt(chart.latest.get(x.key)!) : undefined,
                  }))}
                />
              </div>
              <TimeSeriesChart
                xs={chart.xs}
                series={chart.series}
                formatValue={m.fmt}
                formatTick={formatSI}
                bands={bands}
                height={190}
                ariaLabel={`Biểu đồ ${m.title} theo bộ lọc của ${model.nodeNames.get(node) ?? node}`}
              />
            </section>
          );
        })}
      </div>
    </Card>
  );
}

export function OverviewPage() {
  const { can } = useAuth();
  const live = useLive();
  const now = useNow(1000);
  const nowSec = Math.floor(now / 1000);
  const [metric, setMetric] = useState<TrafficMetric>("pps");
  const [tableView, setTableView] = useState(false);

  const canTelemetry = can(P.telemetryRead);
  const canDdos = can(P.ddosRead);

  const nodes = useQuery({
    queryKey: ["nodes"],
    queryFn: nodesApi.list,
    enabled: can(P.nodesRead),
    refetchInterval: refetchInterval(),
  });
  const blacklistTotal = useQuery({
    queryKey: ["rules", "blacklist", "total"],
    queryFn: () => rulesApi.page("blacklist", { limit: 1 }),
    enabled: can(P.blacklistRead),
    refetchInterval: refetchInterval(),
  });

  // Rebuilt every render: the live model is mutable and re-renders at most
  // once per animation frame, and this is a few hundred points.
  const traffic = buildTrafficChart(live.traffic, metric, nowSec);
  const drops = buildDropsChart(live.traffic, nowSec);
  const fleet = fleetNow(live.traffic, now);
  const bands = attackBands(live.attacks.attacks, now);

  const active = live.attacks.active();
  const recent = live.attacks.attacks.slice(0, 8);
  const fmt = metric === "pps" ? formatPps : formatBps;
  const activeNodes = nodes.data?.filter((n) => !n.revoked).length;
  const hasTraffic = live.traffic.buckets.length > 0;

  return (
    <>
      <PageHeader
        title="Tổng quan"
        description="Lưu lượng đi qua bộ lọc XDP và các cuộc tấn công đang được phát hiện, cập nhật trực tiếp mỗi giây."
      />

      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <StatTile
          hero
          label="Lưu lượng hiện tại"
          icon={<Activity className="size-3.5" aria-hidden />}
          value={canTelemetry && fleet.fresh ? formatPps(fleet.pps) : "—"}
          sub={canTelemetry ? (fleet.fresh ? `${fleet.nodes} node đang báo cáo` : "Chưa có dữ liệu mới") : "Cần quyền telemetry.read"}
        />
        <StatTile
          label="Băng thông"
          icon={<Gauge className="size-3.5" aria-hidden />}
          value={canTelemetry && fleet.fresh ? formatBps(fleet.bps) : "—"}
          sub="Tổng qua bộ lọc default"
        />
        <StatTile
          label="Gói bị chặn / giây"
          icon={<Ban className="size-3.5" aria-hidden />}
          value={canTelemetry && fleet.fresh ? formatSI(fleet.dropped) : "—"}
          sub="Vượt ngưỡng pps + băng thông"
        />
        <StatTile
          label="Tấn công hiện tại"
          tone={active.length > 0 ? "critical" : "neutral"}
          icon={
            active.length > 0 ? (
              <ShieldAlert className="size-3.5 text-critical-ink" aria-hidden />
            ) : (
              <ShieldCheck className="size-3.5 text-good-ink" aria-hidden />
            )
          }
          value={canDdos ? formatInt(active.length) : "—"}
          sub={canDdos ? (active.length > 0 ? "Xem trang Tấn công DDoS" : "Không có bất thường") : "Cần quyền ddos.read"}
        />
        <StatTile
          label="Node"
          icon={<Server className="size-3.5" aria-hidden />}
          value={activeNodes !== undefined ? formatInt(activeNodes) : "—"}
          sub={nodes.data ? `${nodes.data.length - (activeNodes ?? 0)} đã thu hồi` : "Node Edge đã đăng ký"}
        />
        <StatTile
          label="Mục blacklist"
          icon={<Ban className="size-3.5" aria-hidden />}
          value={blacklistTotal.data?.total != null ? formatInt(blacklistTotal.data.total) : "—"}
          sub="IP và dải CIDR đang chặn"
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        <Card className="xl:col-span-2">
          <CardHeader
            title={metric === "pps" ? "Lưu lượng theo node (gói/giây)" : "Băng thông theo node (bit/giây)"}
            description={`Tối đa ${live.traffic.windowSec / 60} phút gần nhất · vùng đỏ là thời gian bị tấn công`}
            actions={
              <>
                <Segmented
                  label="Đơn vị"
                  value={metric}
                  onChange={setMetric}
                  options={[
                    { value: "pps", label: "pps" },
                    { value: "bps", label: "bps" },
                  ]}
                />
                <Button
                  size="sm"
                  variant={tableView ? "secondary" : "ghost"}
                  icon={<Table2 className="size-3.5" />}
                  onClick={() => setTableView((v) => !v)}
                  aria-pressed={tableView}
                >
                  Dạng bảng
                </Button>
              </>
            }
          />
          <div className="px-4 py-3">
            {!canTelemetry ? (
              <Forbidden what="xem lưu lượng (telemetry.read)" />
            ) : !hasTraffic ? (
              <EmptyState
                icon={<Activity className="size-6" aria-hidden />}
                title="Đang chờ dữ liệu lưu lượng"
                description="Biểu đồ xuất hiện khi Master gửi mẫu đầu tiên (mỗi giây một lần cho mỗi node)."
              />
            ) : (
              <>
                {traffic.series.length > 1 && (
                  <div className="mb-2">
                    <Legend
                      items={traffic.series.map((s) => ({
                        key: s.key,
                        label: s.label,
                        colorVar: s.colorVar,
                        value: traffic.latest.has(s.key) ? fmt(traffic.latest.get(s.key)!) : undefined,
                      }))}
                    />
                  </div>
                )}
                {tableView ? (
                  <ChartTable xs={traffic.xs} series={traffic.series} format={fmt} />
                ) : (
                  <TimeSeriesChart
                    xs={traffic.xs}
                    series={traffic.series}
                    formatValue={fmt}
                    formatTick={formatSI}
                    bands={bands}
                    area
                    height={260}
                    ariaLabel="Biểu đồ lưu lượng theo node"
                  />
                )}
              </>
            )}
          </div>
        </Card>

        <Card>
          <CardHeader
            title="Sự kiện DDoS gần đây"
            actions={
              <Link to="/attacks" className="text-[13px] font-medium text-accent-text hover:underline">
                Xem tất cả
              </Link>
            }
          />
          {!canDdos ? (
            <Forbidden what="xem sự kiện DDoS (ddos.read)" />
          ) : recent.length === 0 ? (
            <EmptyState
              icon={<ShieldCheck className="size-6" aria-hidden />}
              title="Chưa phát hiện tấn công nào"
              description="Sự kiện được ghi nhận từ khi mở bảng điều khiển trên trình duyệt này."
            />
          ) : (
            <ul className="divide-y divide-line">
              {recent.map((a) => (
                <li key={a.id}>
                  <Link to={`/attacks?id=${encodeURIComponent(a.id)}`} className="flex flex-col gap-1 px-4 py-2.5 hover:bg-surface-2">
                    <div className="flex items-center gap-2">
                      <AttackStateBadge attack={a} />
                      <span className="truncate text-[13px] font-medium">{a.nodeName}</span>
                      <span className="ml-auto text-xs text-ink-3">{formatAgo(a.startedAtMs ?? a.endedAtMs ?? now, now)}</span>
                    </div>
                    <div className="flex gap-3 text-xs text-ink-2">
                      <span>Bộ lọc {a.filterName || "—"}</span>
                      {a.startPps !== undefined && <span className="tabular">{formatPps(a.startPps)}</span>}
                      {surgeFactor(a) && <span className="text-ink-3">{surgeFactor(a)} so với nền</span>}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {canTelemetry && hasTraffic && <FilterCharts model={live.traffic} nowSec={nowSec} bands={bands} />}

        <Card className="xl:col-span-3">
          <CardHeader
            title="Gói bị chặn mỗi giây"
            description="Theo lý do chặn, cộng dồn mọi node và bộ lọc"
          />
          <div className="px-4 py-3">
            {!canTelemetry ? (
              <Forbidden what="xem lưu lượng (telemetry.read)" />
            ) : !hasTraffic ? (
              <EmptyState title="Đang chờ dữ liệu" />
            ) : (
              <>
                <div className="mb-2">
                  <Legend
                    items={drops.series.map((s) => ({
                      key: s.key,
                      label: s.label,
                      colorVar: s.colorVar,
                      value: formatSI(drops.latest.get(s.key) ?? 0),
                    }))}
                  />
                </div>
                {tableView ? (
                  <ChartTable xs={drops.xs} series={drops.series} format={formatSI} />
                ) : (
                  <TimeSeriesChart
                    xs={drops.xs}
                    series={drops.series}
                    formatValue={(v) => `${formatSI(v)} gói/s`}
                    formatTick={formatSI}
                    bands={bands}
                    height={180}
                    ariaLabel="Biểu đồ gói bị chặn mỗi giây"
                  />
                )}
              </>
            )}
          </div>
        </Card>
      </div>
    </>
  );
}
